import type { SchemadrawImperativeAPI } from "../../utils/types";
import {
  exportToBlob,
  MIME_TYPES,
} from "../../common";
import { DiagramToCodePlugin } from "./DiagramToCodePlugin/DiagramToCodePlugin"
import { TTDDialog } from "./TTDDialog/TTDDialog"
import { getTextFromElements } from "../../element/textElement"
import { getDataURL } from "../../data/blob";
import { safelyParseJSON } from "../../utils/utils";
import { BACKEND_API_URL } from "../../utils/constants"
import { t } from "../../utils/i18n";


export const AIComponents = ({
  excalidrawAPI,
}: {
  excalidrawAPI: SchemadrawImperativeAPI;
}) => {
  return (
    <>
      <DiagramToCodePlugin
        generate={async ({ frame, children }) => {
          const appState = excalidrawAPI.getAppState();

          const blob = await exportToBlob({
            elements: children,
            appState: {
              ...appState,
              exportBackground: true,
              viewBackgroundColor: appState.viewBackgroundColor,
            },
            exportingFrame: frame,
            files: excalidrawAPI.getFiles(),
            mimeType: MIME_TYPES.jpg,
          });

          const dataURL = await getDataURL(blob);

          const textFromFrameChildren = getTextFromElements(children);

          const response = await fetch(BACKEND_API_URL.LLM_TEXT_MERMAID_URL,
            {
              method: "POST",
              credentials: 'include',
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                texts: textFromFrameChildren,
                image: dataURL,
                theme: appState.theme,
              }),
            },
          );

          if (!response.ok) {
            const text = await response.text();
            const errorJSON = safelyParseJSON(text);

            if (!errorJSON) {
              throw new Error(text);
            }

            if (errorJSON.statusCode === 429) {
              return {
                html: `<html>
                <body style="margin: 0; text-align: center">
                <div style="display: flex; align-items: center; justify-content: center; flex-direction: column; height: 100vh; padding: 0 60px">
                  <div style="color:red">Too many requests today,</br>please try again tomorrow!</div>
                  </br>
                  </br>
                  <div>You can also try <a href="https://www.procviz.com
                  }/plus?utm_source=excalidraw&utm_medium=app&utm_content=d2c" target="_blank" rel="noreferrer noopener">Excalidraw+</a> to get more requests.</div>
                </div>
                </body>
                </html>`,
              };
            }

            throw new Error(errorJSON.message || text);
          }

          try {
            const { html } = await response.json();

            if (!html) {
              throw new Error("Generation failed (invalid response)");
            }
            return {
              html,
            };
          } catch (error: any) {
            throw new Error("Generation failed (invalid response)");
          }
        }}
      />

      <TTDDialog
        onTextSubmit={async (input) => {
          try {
            const response = await fetch(BACKEND_API_URL.LLM_TEXT_MERMAID_URL,
              {
                method: "POST",
                credentials: 'include',
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ prompt: input }),
              },
            );

            const json = await response.json();
            let rateLimit = 0;
            let rateUsed = 0;

            if (!response.ok) {
              throw new Error(json.message || "Generation failed...");
            }

            if (json.code === 1002) {
              return {
                rateLimit,
                rateUsed,
                error: new Error(
                  t("mermaid.loginRequest"),
                ),
              };
            }

            const generatedResponse = json.info.result;
            rateLimit = json.info.limit;
            rateUsed = json.info.used;

            if (rateLimit === rateUsed) {
              return {
                rateLimit,
                rateUsed,
                error: new Error(
                  t("mermaid.exceedLimit"),
                ),
              };
            }

            if (!generatedResponse) {
              throw new Error("Generation failed...");
            }

            return { generatedResponse, rateLimit, rateUsed };
          } catch (err: any) {
            throw new Error("Request failed");
          }
        }}
      />
    </>
  );
};
